<script setup lang="ts">
/**
 * The channel logo, as a link that opens this customer's chat on the platform itself in a new tab (LINE OA Manager,
 * Meta Business Suite, the Facebook/Instagram post of a comment) — owner 27 ก.ย. 69 "เพิ่มคลิ้ก platform แล้วพาไป
 * หน้า platform นั้นและลูกค้าคนนั้น". Web chat and imported history have no platform page: just the logo.
 * LINE / Messenger / Instagram chats (lib/platformChat.ts): neither app can be opened on one customer's chat from the
 * ids their APIs give, so the link is what people pasted — the customer's own chat once staff linked it, else the
 * OA's chat list / the Page's inbox + the customer's name copied for its search box; LINE OA not set yet → the owner
 * gets a link to ตั้งค่า › ช่องทางที่เชื่อม, everyone else the logo with a tooltip.
 * Comments: the href is our own API (lib/platformLink.ts), which redirects to the post.
 */
import { computed, watch } from 'vue'
import { RouterLink } from 'vue-router'
import type { Channel, ConversationKind } from '../../api/types'
import { useMock } from '../../api'
import { chatLogoView, copiedToast, copyInClick, usesPastedLink } from '../../lib/platformChat'
import { platformLinkHref, platformLinkInfo } from '../../lib/platformLink'
import { useInbox } from '../../stores/inbox'
import { usePlatformChat } from '../../stores/platformChat'
import { useSession } from '../../stores/session'
import ChannelBadge from '../ui/ChannelBadge.vue'
import Icon from '../ui/Icon.vue'

const props = defineProps<{
  convId: string
  channel: Channel
  kind?: ConversationKind
  history?: boolean
  /** the customer's (LINE group's) name: copied for the search box when the logo opens the chat list / inbox */
  name?: string
}>()
const info = computed(() => platformLinkInfo(props.channel, props.kind, !!props.history))
const href = computed(() =>
  platformLinkHref(props.convId, props.channel, { mock: useMock, base: import.meta.env.VITE_API_BASE ?? '' }),
)

// ---- LINE / Messenger / Instagram: pasted links
const pasted = computed(() => usesPastedLink(props.channel, props.kind, !!props.history))
const chats = usePlatformChat()
const session = useSession()
// asked again each time a chat is opened (a colleague may have linked it meanwhile); the customer panel shares it
watch(
  () => [props.convId, pasted.value] as const,
  ([id, on]) => {
    if (on) void chats.ensure(id, true)
  },
  { immediate: true },
)
const raw = computed(() => (pasted.value ? chats.states[props.convId] : undefined))
const url = computed(() => (raw.value && raw.value !== 'error' ? raw.value.url : null))
const view = computed(() => (pasted.value ? chatLogoView(raw.value, props.channel, props.kind, session.me?.role) : null))

function onClick(): void {
  const name = props.name?.trim()
  if (!view.value?.copyName || !name) return
  const inbox = useInbox()
  const { channel, kind } = props
  void copyInClick(name).then((ok) => inbox.flash(copiedToast(ok, channel, kind)))
}
</script>

<template>
  <template v-if="pasted">
    <a
      v-if="view?.kind === 'link' && url"
      class="plk"
      :href="url"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="`${view.title} (เปิดแท็บใหม่)`"
      @click="onClick"
    >
      <ChannelBadge :channel="channel" :kind="kind" bare />
      <span class="plk-ext" aria-hidden="true"><Icon name="external" /></span>
      <span class="plk-tip" aria-hidden="true">{{ view.title }}</span>
    </a>
    <RouterLink
      v-else-if="view?.kind === 'setup'"
      class="plk is-setup"
      :to="{ name: 'config', params: { section: 'channels' } }"
      :aria-label="view.title"
    >
      <ChannelBadge :channel="channel" :kind="kind" bare />
      <span class="plk-ext" aria-hidden="true"><Icon name="settings" /></span>
      <span class="plk-tip" aria-hidden="true">{{ view.title }}</span>
    </RouterLink>
    <span v-else-if="view?.kind === 'none'" class="plk is-off" tabindex="0" role="img" :aria-label="view.title">
      <ChannelBadge :channel="channel" :kind="kind" bare />
      <span class="plk-tip" aria-hidden="true">{{ view.title }}</span>
    </span>
    <ChannelBadge v-else :channel="channel" :kind="kind" />
  </template>
  <a
    v-else-if="info"
    class="plk"
    :href="href"
    target="_blank"
    rel="noopener noreferrer"
    :aria-label="`${info.title} (เปิดแท็บใหม่)`"
  >
    <ChannelBadge :channel="channel" :kind="kind" bare />
    <span class="plk-ext" aria-hidden="true"><Icon name="external" /></span>
    <!-- own tooltip (not title=): shows at once on hover / keyboard focus, and says where the tab goes -->
    <span class="plk-tip" aria-hidden="true">{{ info.title }}</span>
  </a>
  <ChannelBadge v-else :channel="channel" :kind="kind" />
</template>

<style scoped>
.plk {
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 24px;
  padding: 0 5px 0 3px;
  margin: 0 -2px;
  border-radius: 7px;
  color: var(--mut);
  text-decoration: none;
}
.plk:hover,
.plk:focus-visible {
  background: #eef2f0;
  color: var(--ink);
}
.plk:focus-visible {
  outline: 2px solid var(--ac);
  outline-offset: 1px;
}
/* LINE OA not set up yet (staff view) / state not loaded: the logo says so on hover, it is not a link */
.plk.is-off {
  cursor: default;
}
.plk.is-off:hover {
  background: none;
}
.plk-ext {
  display: inline-flex;
}
.plk-tip {
  position: absolute;
  top: calc(100% + 6px);
  left: -4px;
  z-index: 30;
  width: max-content;
  max-width: 280px;
  padding: 5px 9px;
  border-radius: 7px;
  background: #17211c;
  color: #fff;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 19px;
  white-space: normal;
  box-shadow: 0 6px 18px rgba(23, 33, 28, 0.18);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.12s ease 0s, visibility 0s linear 0.12s;
}
.plk:hover .plk-tip,
.plk:focus-visible .plk-tip {
  opacity: 1;
  visibility: visible;
  transition-delay: 0.15s, 0s;
}
.plk-ext :deep(.ic) {
  width: 12px;
  height: 12px;
  stroke-width: 2.2;
}
</style>
